import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { Radio } from 'components/Radio'
import { GTBookingV2PaymentProcessor, GTBookingV2Refund, GTRefundDestinationInput } from 'gql/types.generated'
import { ArrowRightCircleIcon } from 'components/Icons'
import { TPaidPayment } from 'features/Bookings/components/CancelTicketsModal/types'
import { isEmpty } from 'lodash'

const InputContainer = styled.div`
  padding: 24px 16px;
  border: 1px solid #dcddf1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const InputsContainer = styled.div`
  ${InputContainer}:first-child {
    border-bottom: none;
    border-radius: 8px 8px 0 0;
  }
  ${InputContainer}:last-child {
    border-radius: 0 0 8px 8px;
  }
`

const Text = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #28265d;
`

const CardRefundWrapper = styled.div`
  display: flex;
  gap: 5px 16px;
  align-items: center;
  font-size: 12px;
  line-height: 18px;
  color: #28265d;
  font-weight: 400;
  flex-wrap: wrap;
`

const CardLast4 = styled.span`
  font-weight: 600;
`

const RadioText = styled.div``

export interface ISelectRefundDestinationProps {
  setRefundDestination: (refundDestination: GTRefundDestinationInput) => void
  refundDestination: GTRefundDestinationInput
  last4?: string
  paidPayments: TPaidPayment[]
  bookingRefunds: GTBookingV2Refund[]
  canRefundToKredits: boolean
}

const StyledInfoIcon = styled(ArrowRightCircleIcon)`
  fill: #1976d2;
  width: 20px;
  min-width: 20px;
  height: 20px;
`

const NotificationContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #1976d2;
  background-color: #d9edf7;
  border: 1px solid #bcdff1;
  border-radius: 8px;
  padding: 16px;
  font-size: 12px;
`

const getPaymentAmountRemaining = (payment: TPaidPayment, refunds: GTBookingV2Refund[]): number => {
  const amountRefunded =
    refunds?.reduce((result, refund) => {
      if (refund.paymentID === payment?.id) {
        return result + Math.abs(refund.amount)
      }
      return result
    }, 0) ?? 0
  return (payment?.amount ?? 0) - amountRefunded
}

export const SelectRefundDestination = ({
  refundDestination,
  setRefundDestination,
  last4,
  paidPayments,
  bookingRefunds,
  canRefundToKredits
}: ISelectRefundDestinationProps): ReactElement => {
  const isCardRefundDestinationSelected = refundDestination === GTRefundDestinationInput.Card
  const conektaPayment = paidPayments.find((payment) => payment.processor === GTBookingV2PaymentProcessor.Conekta)
  const clipPayment = paidPayments.find((payment) => payment.processor === GTBookingV2PaymentProcessor.Clip)
  const hasClipPayment = !isEmpty(clipPayment)
  const hasConektaPayment = !isEmpty(conektaPayment)
  const kreditPayment = paidPayments.find((payment) => payment.processor === GTBookingV2PaymentProcessor.Kredit)
  const hasKreditPayment = !isEmpty(kreditPayment)

  const isPaidWithKreditsAndConekta =
    paidPayments.every(
      (payment) =>
        payment.processor === GTBookingV2PaymentProcessor.Kredit ||
        payment.processor === GTBookingV2PaymentProcessor.Conekta
    ) &&
    hasConektaPayment &&
    hasKreditPayment

  const isPaidWithKreditsAndClip =
    paidPayments.every(
      (payment) =>
        payment.processor === GTBookingV2PaymentProcessor.Kredit ||
        payment.processor === GTBookingV2PaymentProcessor.Clip
    ) &&
    hasClipPayment &&
    hasKreditPayment

  const getCardRefundAmountRemaining = (): number => {
    if (conektaPayment) {
      return getPaymentAmountRemaining(conektaPayment, bookingRefunds)
    } else if (clipPayment) {
      return getPaymentAmountRemaining(clipPayment, bookingRefunds)
    }
    return 0
  }

  const canSelectCardDestination = getCardRefundAmountRemaining() > 0
  return (
    <>
      <InputsContainer>
        <InputContainer>
          <Radio
            id={GTRefundDestinationInput.Kredit}
            name="refundDestination"
            checked={refundDestination === GTRefundDestinationInput.Kredit}
            disabled={!canRefundToKredits}
            onChange={(): void => {
              setRefundDestination(GTRefundDestinationInput.Kredit)
            }}
            purple
            noMargin
          >
            <RadioText>
              <Text>
                <FormattedMessage
                  id="myBookings.cancelTicketsModal.selectRefundDestination.kolorsCredits"
                  defaultMessage="Reembolso inmediato a Kolors Credits"
                />
              </Text>
            </RadioText>
          </Radio>
        </InputContainer>
        <InputContainer>
          <Radio
            id={GTRefundDestinationInput.Card}
            name="refundDestination"
            checked={isCardRefundDestinationSelected}
            onChange={(): void => {
              setRefundDestination(GTRefundDestinationInput.Card)
            }}
            purple
            noMargin
            disabled={!canSelectCardDestination}
            flexStart
          >
            <RadioText>
              <CardRefundWrapper>
                <Text>
                  <FormattedMessage
                    id="myBookings.cancelTicketsModal.selectRefundDestination.bankAccount"
                    defaultMessage="Reembolso a cuenta de 5 a 30 días hábiles"
                  />
                </Text>
                {last4 && (
                  <div>
                    <FormattedMessage
                      id="myBookings.cancelTicketsModal.selectRefundDestination.cardInfo"
                      defaultMessage="Tarjeta {last4}"
                      values={{ last4: <CardLast4>**** {last4}</CardLast4> }}
                    />
                  </div>
                )}
              </CardRefundWrapper>
            </RadioText>
          </Radio>
          {isCardRefundDestinationSelected && isPaidWithKreditsAndConekta && (
            <NotificationContainer>
              <StyledInfoIcon />
              <FormattedMessage
                id="myBookings.cancelTicketsModal.selectRefundDestination.mixConeKtaPayment.notification"
                defaultMessage="Tu reservación fue pagada de manera mixta, te haremos la devolución proporcional a cada método de pago del cobro inicial."
              />
            </NotificationContainer>
          )}
          {isCardRefundDestinationSelected && isPaidWithKreditsAndClip && (
            <NotificationContainer>
              <StyledInfoIcon />
              <FormattedMessage
                id="myBookings.cancelTicketsModal.selectRefundDestination.mixClipPayment.notification"
                defaultMessage="Si seleccionas esta opción, debes realizar la devolución manualmente, directo en Clip"
              />
            </NotificationContainer>
          )}
        </InputContainer>
      </InputsContainer>
    </>
  )
}
